import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import SearchInput from "../../components/Forms/SearchInput";
import { items, orderStatus, SELECTED_CUSTOMER_STORAGE_KEY } from "../../constant";
import { useApiCall } from "../../services/api";
import { formatDate, handleRouteNav, roundDecimal } from "../../utils";
import Loader from "../../common/Loader";
import Filters from "../../components/Filters";
import { Card, Select } from "antd";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { PiRowsPlusTopFill } from "react-icons/pi";
import PrimaryButton from "../../components/Forms/PrimaryButton";
import { useGlobalModal } from "../../context/SuccessModalProvider";
import { useInfiniteListWithScrollRestore } from "../../hooks/useInfiniteListWithScrollRestore";
import { OrderMorePagesButtons } from "../Orders";
import AgentClientSelect from "../../components/AgentClientSelect";

const PAGINATION_STORAGE_PREFIX = "pagination:orders";
const PER_PAGE = 12;

const CustomerOrders = () => {
  const { postApi } = useApiCall();
  const { appliedFilters, DrawerUI, FilterButton, resetFilters, hasFilters } = Filters({
    filters: [
      {
        type: "select",
        key: "status",
        label: "Order Status",
        options: Object.entries(orderStatus).map(([key, value]) => ({
          label: value,
          value: value,
        })),
      },
      {
        type: "daterange",
        key: "orderPlaced",
        label: "Order Placed",
      },
      {
        type: "daterange",
        key: "dispatchDate",
        label: "Dispatch Date",
      },
      {
        type: "select",
        key: "quantity",
        label: "Total Quantity",
        options: [
          { label: "< 1000m", value: "-1000" },
          { label: "1000 - 5000m", value: "1000-5000" },
          { label: "5000 - 10000m", value: "5000-10000" },
          { label: "> 10000m", value: "10000-" },
        ],
      },
    ],
    onChange: () => resetList(),
  });

  const { is_sales_person, user_id, is_customer, role_id, customer_id, company_name, is_agent } = useSelector((state) => state.auth);

  const [customerListLoading, setCustomerListLoading] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    const stored = sessionStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY);
    if (stored === null || stored === "") return customer_id;
    return Number.isNaN(Number(stored)) ? stored : Number(stored);
  });

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const customerSelectRef = useRef(null);
  const hasInitializedCustomerRef = useRef(false);
  const { showPopup } = useGlobalModal();

  const filtersSignature = useMemo(() => JSON.stringify(appliedFilters || {}), [appliedFilters]);

  const fetchPage = useCallback(
    async (page) => {
      const { status, quantity, orderPlaced, dispatchDate } = appliedFilters;
      const [minTotalQty, maxTotalQty] = quantity ? quantity.split("-").map(Number) : [];

      const filters = {
        status,
        minTotalQty: minTotalQty || undefined,
        maxTotalQty: maxTotalQty || undefined,
        start_date: orderPlaced?.[0] ? formatDate(new Date(orderPlaced[0]), { format: "YYYY-MM-DD" }) : undefined,
        end_date: orderPlaced?.[1] ? formatDate(new Date(orderPlaced[1]), { format: "YYYY-MM-DD" }) : undefined,
        start_dispatch_date: dispatchDate?.[0] ? formatDate(new Date(dispatchDate[0]), { format: "YYYY-MM-DD" }) : undefined,
        end_dispatch_date: dispatchDate?.[1] ? formatDate(new Date(dispatchDate[1]), { format: "YYYY-MM-DD" }) : undefined,
      };

      const response = await postApi(
        "order/get-order-with-customer-id",
        {
          customer_id: is_agent ? selectedCustomerId || customer_id : customer_id,
          search: query,
          page,
          per_page: PER_PAGE,
          ...(Object.keys(appliedFilters).length ? filters : {}),
          ...(is_sales_person ? { user_id } : {}),
        },
        null,
      );

      if (!response.status && (response.is_blocked || response.is_deleted)) {
        showPopup({
          title: null,
          content: <span className="custom-modal-title">{response.message}</span>,
          onOk: () => console.log("Confirmed!"),
          className: "custom-success-modal",
        });
        return { rows: [], total: 0, perPage: PER_PAGE };
      }

      const formattedRows = (response?.data || []).map((order, index) => {
        const uniquePids = new Set();
        order.items?.forEach((item) => uniquePids.add(item.sort_no));

        let first_item_name = order.items[0]?.short_name;
        const total_qty = order.items.reduce((t, i) => t + (+i.update_total_meter || +i.quantity_meters), 0);

        return {
          ...order,
          clientName: order.customer?.company_name || "-",
          first_item_name,
          more_products: uniquePids.size > 1 ? `+ ${uniquePids.size - 1} more` : null,
          totalCases: order.items.reduce((t, i) => t + (+i.update_total_cases || +i.cases), 0),
          totalQuantityMeters: roundDecimal(total_qty),
        };
      });

      return { rows: formattedRows, total: response?.total || 0, perPage: PER_PAGE };
    },
    [appliedFilters, customer_id, is_agent, is_sales_person, query, selectedCustomerId, user_id],
  );

  const {
    listData: orderData,
    totalCount: totalOrders,
    loading,
    isFetchingMore,
    loaderRef,
    handleItemClick,
    resetList,
    hasRestoredStateRef,
  } = useInfiniteListWithScrollRestore({
    storageKeyPrefix: PAGINATION_STORAGE_PREFIX,
    extraKey: selectedCustomerId || customer_id,
    query,
    filtersSignature,
    fetchPage,
  });

  useEffect(() => {
    if (!is_agent) return;

    const fetchCustomersForAgent = async () => {
      setCustomerListLoading(true);
      try {
        const response = await postApi("sales/agent/agent-customer-list", { agent_customer_id: customer_id });
        const customers = response?.data || [];
        const hasSelf = customers.some((item) => String(item.customer_id) === String(customer_id));

        const normalizedCustomers = hasSelf ? customers : [{ customer_id, company_name: company_name || "Self" }, ...customers];

        setAllCustomers(normalizedCustomers);

        const stored = sessionStorage.getItem(SELECTED_CUSTOMER_STORAGE_KEY);
        const normalizedStored = stored === null || stored === "" ? null : Number.isNaN(Number(stored)) ? stored : Number(stored);
        const hasStoredInList = normalizedCustomers.some((item) => String(item.customer_id) === String(normalizedStored));
        const nextCustomerId = hasStoredInList ? normalizedStored : customer_id;

        setSelectedCustomerId(nextCustomerId);
        sessionStorage.setItem(SELECTED_CUSTOMER_STORAGE_KEY, String(nextCustomerId));
      } finally {
        setCustomerListLoading(false);
      }
    };
    fetchCustomersForAgent();
  }, [is_agent, customer_id, company_name]);

  const handleOnSearchChange = (e) => {
    const value = e.target.value;
    setSearchParams(
      (prev) => {
        if (value) prev.set("q", value);
        else prev.delete("q");
        return prev;
      },
      { replace: true },
    );
  };

  const handleRow = (record) => {
    handleItemClick(record.order_id);
    navigate(handleRouteNav(`/orders/${record.order_id}`, role_id));
  };

  const { setTitle, setDescription, setBackButton } = useOutletContext();

  useEffect(() => {
    if (!is_customer) {
      setTitle("Orders");
      setDescription(`${totalOrders} total orders`);
      setBackButton(false);
    }
  }, [setTitle, setDescription, setBackButton, totalOrders]);

  // Reset pagination/list when the agent switches the selected customer,
  // but skip the very first run (initial mount) and skip if we just restored state.
  useEffect(() => {
    if (!hasInitializedCustomerRef.current) {
      hasInitializedCustomerRef.current = true;
      return;
    }
    if (hasRestoredStateRef.current) return;

    resetFilters();
    setSearchParams(
      (prev) => {
        prev.delete("q");
        return prev;
      },
      { replace: true },
    );
    resetList();
  }, [selectedCustomerId]);

  return (
    <>
      <DrawerUI />
      <div className="bg-[#FFFFFF] mt-3" style={{ borderRadius: "32px", overflow: "hidden" }}>
        <div className="flex justify-between gap-7 items-center px-8 py-6">
          <div className="flex items-center gap-3 w-full">
            <SearchInput value={query} onChange={handleOnSearchChange} placeholder="Search for orders" />
          </div>
          <div className="flex gap-3 items-center">
            <FilterButton />
            {hasFilters && <PrimaryButton btnType="secondary" text="Remove" onClick={() => resetFilters()} textClassName="" className="!w-[130px]" />}
          </div>
        </div>
        <div className="flex w-full justify-between gap-3 px-8">
          <OrderMorePagesButtons activeButton="" selectedClient={selectedCustomerId} />
          <AgentClientSelect
            selectedCustomerId={selectedCustomerId}
            onClientChange={(newId) => {
              setSelectedCustomerId(newId);
            }}
          />
        </div>
        <>
          {orderData?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-4 pt-0 px-6 pb-6">
              {orderData.map((item, i) => (
                <div
                  id={`list-item-${item.order_id}`}
                  className="bg-white rounded-xl flex flex-row border hover:shadow-lg duration-300 cursor-pointer w-full min-w-0"
                  key={i}
                  onClick={() => handleRow(item)}
                >
                  <div className="min-w-[150px] w-[150px] h-auto overflow-hidden rounded-l-xl flex items-center p-2">
                    <img
                      src={item?.items?.[0]?.product_picture_url || "/assets/images/place_holder_image1.svg"}
                      alt="card-logo"
                      className="w-full h-full rounded-xl flex-1 object-cover shrink-0"
                      onError={(e) => {
                        const img = e.currentTarget;
                        const fallback = "/assets/images/place_holder_image1.svg";
                        if (img && !String(img.src).includes("place_holder_image1.svg")) {
                          img.onerror = null;
                          img.src = fallback;
                        }
                      }}
                    />
                  </div>
                  <div className="p-4 flex flex-col justify-between gap-5 flex-1 min-w-0 text-start">
                    <div className="w-full min-w-0 flex flex-col">
                      <Paragraph className="!mb-0">Order ID: #{item.order_id}</Paragraph>
                      <Title
                        level={5}
                        className="!m-0 flex items-center w-full min-w-0"
                        title={`${item.first_item_name}${item.more_products ? ` ${item.more_products}` : ""}`}
                      >
                        <span className="truncate min-w-0">{item.first_item_name}</span>
                        <span className="shrink-0 whitespace-nowrap">&nbsp;{item.more_products}</span>
                      </Title>
                    </div>
                    <div className="flex flex-col gap-3">
                      {item.status === orderStatus.production && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img src="/assets/icons/GearFine.png" alt="Production" className="w-auto h-[30px] object-contain" />
                          </div>
                          <div className="flex flex-col items-center">
                            <span className="text-gray-500">In Production</span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.dispatched && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img src="/assets/icons/TruckTrailer.png" alt="Dispatched" className="w-auto h-[30px] object-contain" />
                          </div>
                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Dispatched:</span>
                            <span className="text-black">{formatDate(item?.dispatch_date, { format: "D MMM YYYY" })}</span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.parts_dispatched && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img src="/assets/icons/TruckTrailer.png" alt="Part Dispatched" className="w-auto h-[30px] object-contain" />
                          </div>
                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Part Dispatched:</span>
                            <span className="text-black">{formatDate(item?.dispatch_date, { format: "D MMM YYYY" })}</span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.cancelled && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img src="/assets/icons/Prohibit.png" alt="Cancelled" className="w-auto h-[30px] object-contain" />
                          </div>
                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Cancelled:</span>
                            <span className="text-black">{formatDate(item?.cancel_date, { format: "D MMM YYYY" })}</span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.parts_cancelled && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img src="/assets/icons/Prohibit.png" alt="Part Cancelled" className="w-auto h-[30px] object-contain" />
                          </div>
                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Part Cancelled:</span>
                            <span className="text-black">{formatDate(item?.cancel_date, { format: "D MMM YYYY" })}</span>
                          </div>
                        </div>
                      )}

                      {item.status === orderStatus.placed && (
                        <div className="flex gap-3 items-center">
                          <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                            <img src="/assets/icons/cube.png" alt="Placed" className="w-auto h-[30px] object-contain" />
                          </div>
                          <div className="flex flex-col text-start">
                            <span className="text-gray-500">Order Placed:</span>
                            <span className="text-black">{formatDate(item?.created_at, { format: "D MMM YYYY" })}</span>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-3 items-center">
                        <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                          <PiRowsPlusTopFill className="w-auto h-[30px] object-contain text-[#A33434]" />
                        </div>
                        <div className="flex flex-col text-start">
                          <span className="text-gray-500">Total Quantity:</span>
                          <span className="text-black">{`${item.totalCases} cases (${item.totalQuantityMeters}m)`}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {orderData?.length === 0 && !loading && (
            <div className="flex justify-center">
              <div>
                <img src="/assets/icons/EmptySearch.svg" alt="Empty" />
                <p className="text-[#000] mb-8">Sorry! We couldn't find any matches.</p>
              </div>
            </div>
          )}
        </>

        <div ref={loaderRef} className="flex justify-center py-6">
          {(loading || isFetchingMore) && <Loader size={40} />}
        </div>
      </div>
    </>
  );
};

export default CustomerOrders;
